/**
 * F14.2 — Pill com bandeira + label + chevron, abre dropdown com as
 * 3 opções (FR/PT/EN). Estilo alinhado ao /design-system: pill,
 * border lime-border, fundo navy-soft, glow lime no hover.
 *
 * Pre-auth (Login) usa apenas localStorage via useI18n().
 * Pós-auth (Header global, F14.5), o chamador pode passar
 * `onChange` adicional para chamar `users.updateLocale` via tRPC.
 */

import { useEffect, useRef, useState } from "react";
import { LOCALE_LABELS, LOCALES, type Locale } from "../../i18n/messages.js";
import { useI18n } from "../../i18n/I18nProvider.js";

interface LanguageSwitcherProps {
  /** Hook adicional disparado após localStorage atualizar (ex.: tRPC sync). */
  onChange?: (locale: Locale) => void;
  /** Tamanho. Default `default` (40px). */
  size?: "sm" | "default";
}

export function LanguageSwitcher({ onChange, size = "default" }: LanguageSwitcherProps) {
  const { locale, setLocale } = useI18n();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Fecha quando clica fora.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", escHandler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", escHandler);
    };
  }, [open]);

  const height = size === "sm" ? 32 : 40;
  const fontSize = size === "sm" ? 12 : 13;
  const current = LOCALE_LABELS[locale];

  function handlePick(next: Locale) {
    setLocale(next);
    setOpen(false);
    onChange?.(next);
  }

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          height,
          padding: "0 14px",
          background: "var(--navy-soft)",
          color: "#f4f7fc",
          border: "1.5px solid var(--lime-border)",
          borderRadius: "var(--radius-pill)",
          fontFamily: "var(--font-body)",
          fontWeight: 700,
          fontSize,
          letterSpacing: "1px",
          cursor: "pointer",
          transition: "border-color 120ms ease, background 120ms ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "var(--lime)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "var(--lime-border)";
        }}
      >
        <span aria-hidden style={{ fontSize: fontSize + 2 }}>
          {current.flag}
        </span>
        <span>{current.short}</span>
        <span aria-hidden style={{ color: "var(--lime)", fontSize: 12 }}>
          ▾
        </span>
      </button>

      {open && (
        <ul
          role="listbox"
          style={{
            position: "absolute",
            top: height + 6,
            right: 0,
            minWidth: 160,
            background: "var(--navy-elevated)",
            border: "1px solid var(--lime-border)",
            borderRadius: "var(--radius-md)",
            boxShadow: "var(--shadow-lg)",
            padding: 6,
            margin: 0,
            listStyle: "none",
            zIndex: 50,
          }}
        >
          {LOCALES.map((code) => {
            const l = LOCALE_LABELS[code];
            const active = code === locale;
            return (
              <li key={code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => handlePick(code)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 12px",
                    background: active ? "var(--lime-soft)" : "transparent",
                    color: active ? "var(--lime)" : "#c3cfe1",
                    border: "none",
                    borderRadius: "var(--radius-sm)",
                    fontFamily: "var(--font-body)",
                    fontSize: 13,
                    fontWeight: active ? 700 : 500,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) => {
                    if (!active) {
                      e.currentTarget.style.background = "var(--lime-soft)";
                      e.currentTarget.style.color = "var(--lime)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!active) {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = "#c3cfe1";
                    }
                  }}
                >
                  <span aria-hidden style={{ fontSize: 16 }}>
                    {l.flag}
                  </span>
                  <span style={{ flex: 1 }}>{l.label}</span>
                  {active && (
                    <span aria-hidden style={{ color: "var(--lime)" }}>
                      ✓
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
